// UI icon placeholders (art-assets §6.2): 128 × 128, a black shape on transparent (only the alpha
// matters: icons are CSS `mask-image`s coloured with `currentColor`). Each icon is a tiny path
// list in a 128-unit box, drawn with a slightly hand-jittered 12 px line (≥ 10 px at 128 px).

import type { Canvas2D, Ctx2D } from '../../render/canvas.js';
import { art, inkCirclePath, inkPolyPath, TAU } from './draw.js';

/** Every icon name (art-assets §6.2); the file is `ui/icons/<name>.png`. */
export const ICON_NAMES = [
  'settings',
  'close',
  'copy',
  'play',
  'watch',
  'leave',
  'plus',
  'trash',
  'menu',
  'camera',
  'split',
  'shoot',
  'minimize',
  'expand',
  'star',
  'sound-on',
  'sound-off',
  'help',
  'brush',
  'eraser',
  'fill',
  'eyedropper',
  'mirror',
  'undo',
  'redo',
  'clear',
  'import',
  'export',
  'save',
  'edit',
  'zoom-in',
  'zoom-out',
  'guide',
] as const;
export type IconName = (typeof ICON_NAMES)[number];

export function isIconName(v: string): v is IconName {
  return (ICON_NAMES as readonly string[]).includes(v);
}

/**
 * One drawing op in the 128-unit box:
 * - `line` x0 y0 x1 y1, `poly` x y … (open), `loop` x y … (closed): stroked;
 * - `shape` x y …: a filled polygon;
 * - `ring` cx cy r: a stroked circle; `disc` cx cy r: a filled circle;
 * - `arc` cx cy r fromDeg toDeg: a stroked arc (clockwise, 0° = +x, y down);
 * - `rect` x y w h: a stroked rectangle.
 */
export type IconOp =
  | readonly ['line', number, number, number, number]
  | readonly ['poly' | 'loop' | 'shape', ...number[]]
  | readonly ['ring' | 'disc', number, number, number]
  | readonly ['arc', number, number, number, number, number]
  | readonly ['rect', number, number, number, number];

/** The stroke width in box units (art-assets §6.2: ≥ 10 px at 128 px). */
export const ICON_STROKE = 12;

function starPoints(cx: number, cy: number, outer: number, inner: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outer : inner;
    pts.push(
      Math.round((cx + r * Math.cos(a)) * 10) / 10,
      Math.round((cy + r * Math.sin(a)) * 10) / 10,
    );
  }
  return pts;
}

function gearTeeth(): IconOp[] {
  const ops: IconOp[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i * TAU) / 8;
    const c = Math.cos(a);
    const s = Math.sin(a);
    ops.push(['line', 64 + 34 * c, 64 + 34 * s, 64 + 50 * c, 64 + 50 * s]);
  }
  return ops;
}

/** Mirrors ops left↔right (x → 128 − x), e.g. redo from undo. */
export function mirrorIconOps(ops: readonly IconOp[]): IconOp[] {
  return ops.map((op): IconOp => {
    switch (op[0]) {
      case 'line':
        return ['line', 128 - op[1], op[2], 128 - op[3], op[4]];
      case 'ring':
      case 'disc':
        return [op[0], 128 - op[1], op[2], op[3]];
      case 'arc':
        return ['arc', 128 - op[1], op[2], op[3], 180 - op[5], 180 - op[4]];
      case 'rect':
        return ['rect', 128 - op[1] - op[3], op[2], op[3], op[4]];
      default: {
        const [kind, ...pts] = op;
        return [kind, ...pts.map((v, i) => (i % 2 === 0 ? 128 - v : v))];
      }
    }
  });
}

const UNDO: readonly IconOp[] = [
  ['arc', 66, 74, 34, 205, 400],
  ['poly', 22, 40, 34, 62, 58, 52],
];

const SPEAKER: IconOp = ['shape', 20, 50, 42, 50, 64, 30, 64, 98, 42, 78, 20, 78];
const TRAY: IconOp = ['poly', 22, 78, 22, 104, 106, 104, 106, 78];
const LENS: readonly IconOp[] = [
  ['ring', 54, 54, 30],
  ['line', 77, 77, 106, 106],
];

export const ICON_PATHS: Readonly<Record<IconName, readonly IconOp[]>> = Object.freeze({
  settings: [['ring', 64, 64, 28], ['ring', 64, 64, 8], ...gearTeeth()],
  close: [
    ['line', 32, 32, 96, 96],
    ['line', 96, 32, 32, 96],
  ],
  copy: [
    ['rect', 46, 46, 54, 58],
    ['poly', 28, 84, 28, 26, 78, 26],
  ],
  play: [['shape', 38, 24, 104, 64, 38, 104]],
  watch: [
    ['arc', 64, 78.6, 42.6, 200, 340],
    ['arc', 64, 49.4, 42.6, 20, 160],
    ['disc', 64, 64, 14],
  ],
  leave: [
    ['line', 102, 64, 32, 64],
    ['poly', 58, 36, 30, 64, 58, 92],
  ],
  plus: [
    ['line', 64, 28, 64, 100],
    ['line', 28, 64, 100, 64],
  ],
  trash: [
    ['poly', 36, 42, 44, 108, 84, 108, 92, 42],
    ['line', 26, 38, 102, 38],
    ['poly', 52, 36, 52, 22, 76, 22, 76, 36],
  ],
  menu: [
    ['line', 26, 36, 102, 36],
    ['line', 26, 64, 102, 64],
    ['line', 26, 92, 102, 92],
  ],
  camera: [
    ['rect', 18, 42, 92, 58],
    ['ring', 64, 71, 15],
    ['poly', 44, 42, 52, 28, 76, 28, 84, 42],
  ],
  split: [
    ['ring', 38, 94, 13],
    ['ring', 90, 94, 13],
    ['line', 46, 82, 94, 22],
    ['line', 82, 82, 34, 22],
  ],
  shoot: [
    ['disc', 76, 64, 28],
    ['line', 14, 64, 34, 64],
    ['line', 22, 40, 38, 48],
    ['line', 22, 88, 38, 80],
  ],
  minimize: [['shape', 28, 46, 100, 46, 64, 90]],
  expand: [['shape', 46, 28, 90, 64, 46, 100]],
  star: [['shape', ...starPoints(64, 68, 52, 22)]],
  'sound-on': [SPEAKER, ['arc', 64, 64, 22, -50, 50], ['arc', 64, 64, 42, -50, 50]],
  'sound-off': [SPEAKER, ['line', 80, 48, 108, 80], ['line', 108, 48, 80, 80]],
  help: [
    ['arc', 64, 46, 22, 200, 380],
    ['poly', 84.7, 53.5, 64, 70, 64, 82],
    ['disc', 64, 104, 8],
  ],
  brush: [
    ['line', 106, 22, 66, 62],
    ['shape', 70, 58, 74, 74, 52, 98, 20, 108, 30, 78, 54, 56],
  ],
  eraser: [
    ['loop', 26, 82, 72, 36, 104, 68, 58, 114],
    ['line', 48, 60, 80, 92],
  ],
  fill: [
    ['loop', 26, 58, 62, 22, 98, 58, 62, 94],
    ['line', 62, 22, 62, 8],
    ['disc', 106, 94, 11],
  ],
  eyedropper: [
    ['line', 26, 102, 74, 54],
    ['disc', 92, 36, 18],
    ['line', 60, 40, 88, 68],
  ],
  mirror: [
    ['line', 64, 12, 64, 28],
    ['line', 64, 52, 64, 76],
    ['line', 64, 100, 64, 116],
    ['shape', 50, 38, 50, 90, 14, 64],
    ['loop', 78, 38, 78, 90, 114, 64],
  ],
  undo: UNDO,
  redo: mirrorIconOps(UNDO),
  clear: [
    ['line', 100, 16, 66, 58],
    ['shape', 50, 52, 80, 70, 64, 110, 18, 96],
  ],
  import: [TRAY, ['line', 64, 18, 64, 78], ['poly', 40, 56, 64, 80, 88, 56]],
  export: [TRAY, ['line', 64, 84, 64, 20], ['poly', 40, 44, 64, 20, 88, 44]],
  save: [
    ['loop', 22, 22, 90, 22, 106, 38, 106, 106, 22, 106],
    ['rect', 42, 66, 44, 40],
    ['line', 44, 24, 44, 42],
  ],
  edit: [
    ['loop', 26, 102, 30, 80, 84, 26, 102, 44, 48, 98],
    ['line', 72, 38, 90, 56],
  ],
  'zoom-in': [...LENS, ['line', 54, 40, 54, 68], ['line', 40, 54, 68, 54]],
  'zoom-out': [...LENS, ['line', 40, 54, 68, 54]],
  guide: [
    ['ring', 64, 64, 46],
    ['line', 18, 54, 110, 54],
    ['line', 18, 76, 110, 76],
  ],
});

function nums(op: readonly unknown[]): number[] {
  return op.slice(1) as number[];
}

/** Draws one op list (128-unit box) into `ctx`, already scaled. */
export function drawIconOps(
  ctx: Ctx2D,
  ops: readonly IconOp[],
  seed: number,
  color = '#000',
): void {
  ctx.lineWidth = ICON_STROKE;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ops.forEach((op, i) => {
    const s = seed + i * 101;
    const v = nums(op);
    ctx.beginPath();
    switch (op[0]) {
      case 'line':
      case 'poly':
        inkPolyPath(ctx, v, s, 1.2);
        ctx.stroke();
        break;
      case 'loop':
        inkPolyPath(ctx, v, s, 1.2, true);
        ctx.stroke();
        break;
      case 'shape':
        inkPolyPath(ctx, v, s, 1, true);
        ctx.fill();
        ctx.stroke();
        break;
      case 'ring':
        inkCirclePath(ctx, v[0] ?? 64, v[1] ?? 64, v[2] ?? 10, s, 0.03, 48);
        ctx.stroke();
        break;
      case 'disc':
        inkCirclePath(ctx, v[0] ?? 64, v[1] ?? 64, v[2] ?? 10, s, 0.03, 32);
        ctx.fill();
        break;
      case 'arc': {
        const [cx = 64, cy = 64, r = 10, a0 = 0, a1 = 180] = v;
        ctx.arc(cx, cy, r, (a0 * Math.PI) / 180, (a1 * Math.PI) / 180);
        ctx.stroke();
        break;
      }
      case 'rect': {
        const [x = 0, y = 0, w = 0, h = 0] = v;
        inkPolyPath(ctx, [x, y, x + w, y, x + w, y + h, x, y + h], s, 1.2, true);
        ctx.stroke();
        break;
      }
    }
  });
}

/** The placeholder for `ui/icons/<name>.png` (default 128 × 128), or null for an unknown name. */
export function drawIcon(name: string, width = 128, height = width): Canvas2D | null {
  if (!isIconName(name)) return null;
  const ops = ICON_PATHS[name];
  const seed = ICON_NAMES.indexOf(name) * 977 + 5;
  return art(width, height, (ctx, w, h) => {
    const k = Math.min(w, h) / 128;
    ctx.setTransform(k, 0, 0, k, (w - 128 * k) / 2, (h - 128 * k) / 2);
    drawIconOps(ctx, ops, seed);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  });
}
